import { rest } from '@0xtorch/core'
import { z } from 'zod'
import { fetchDataWithAutoRetry } from '../../utils/fetchDataWithAutoRetry'
import { bitbankApiEndpoint } from '../constants'
import { errorResponseSchema } from '../schemas'
import { createPrivateGetHeaders } from '../utils'
import { type Deposit, depositSchema } from './schema'

const responseSchema = z.object({
  success: z.literal(1),
  data: z.object({
    deposits: z.array(depositSchema),
  }),
})

const urlPath = '/v1/user/deposit_history'

type FetchDepositHistoriesParameters = {
  apiKey: string
  apiSecret: string
  asset: string
  from: number
  to: number
  limit: number
  proxy?: string
  headers?: Record<string, string>
}

export const fetchDepositHistories = async ({
  apiKey,
  apiSecret,
  asset,
  from,
  to,
  limit,
  proxy,
  headers,
}: FetchDepositHistoriesParameters): Promise<readonly Deposit[]> =>
  fetchDataWithAutoRetry({
    from,
    to,
    limit,
    desc: true,
    fetchData: async ({ from, to, limit, desc }) => {
      if (!desc) {
        throw new Error('desc must be true')
      }
      if (limit > 100) {
        throw new Error('limit must be less than or equal to 100')
      }

      const url = new URL(urlPath, bitbankApiEndpoint)
      url.searchParams.append('asset', asset)
      url.searchParams.append('count', limit.toString())
      if (from !== undefined) {
        url.searchParams.append('since', from.toString())
      }
      if (to !== undefined) {
        url.searchParams.append('end', to.toString())
      }
      const query = url.searchParams.toString()

      const response = await rest(
        proxy === undefined
          ? url.toString()
          : `${proxy}${encodeURIComponent(url.toString())}`,
        {
          fetchOptions: {
            method: 'GET',
            headers: {
              ...headers,
              ...(await createPrivateGetHeaders({
                apiKey,
                apiSecret,
                urlPath: url.pathname,
                query: query.length > 0 ? `?${query}` : '',
              })),
            },
          },
          schema: z.union([responseSchema, errorResponseSchema]),
        },
      )
      if (response.success === 0) {
        throw new Error(`Error: ${response.data.code}`)
      }

      return response.data.deposits
    },
  })
